import { Component, OnInit, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin, of, Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ApiService } from '@core/services/api.service';
import { HeaderService } from '@core/services/header.service';
import { SnackbarService } from '@core/services/snackbar.service';
import { AuthService } from '@core/services/auth.service';
import { PermissionService } from '@core/services/permission.service';
import { ConfirmDialogService } from '@core/services/confirm-dialog.service';

@Component({
  standalone: false,
  selector: 'app-people-hub',
  templateUrl: './people-hub.component.html',
  styleUrls: ['./people-hub.component.scss']
})
export class PeopleHubComponent implements OnInit {
  private destroyRef = inject(DestroyRef);

  pageTab: 'people' | 'attendance' | 'leaves' = 'people';

  employees: any[]   = [];
  departments: any[] = [];
  teams: any[]       = [];
  shifts: any[]      = [];
  loading            = true;

  searchText   = '';
  filterStatus = 'all';

  selectedEmp: any  = null;
  activeDetailTab   = 'overview';
  detailLoading     = false;

  empAttendance: any[] = [];
  empAttLoading        = false;
  empLeaves: any[]     = [];
  empBalances: any[]   = [];
  empPending: any[]    = [];
  empAssets: any[]     = [];
  empTeams: any[]      = [];

  attMonth: string = this.currentMonthStr();
  attFilter        = 'all';

  rejectingId: string | null = null;
  rejectReason               = '';
  approvingId: string | null = null;
  showAllocate               = false;
  allocateForm: any          = { leaveType: 'casual', days: 0 };
  allocating                 = false;

  dirDate: string      = this.todayStr();
  dirRecords: any[]    = [];
  dirLoading           = false;
  dirSearch            = '';
  dirFilter            = 'all';

  leaveSubTab: 'approvals' | 'balances' = 'approvals';
  allLeaves: any[]         = [];
  allLeavesLoading         = false;
  leaveSearch              = '';
  leaveStatusFilter        = 'all';
  leaveCurrentPage         = 1;
  leavePageSize            = 5;
  leavePageSizeOptions     = [5, 10, 20, 50];
  leaveRejectingId: string | null = null;
  leaveRejectReason        = '';
  leaveApprovingId: string | null = null;

  allBalances: any[]   = [];
  balancesLoading      = false;
  balanceSearch        = '';

  showBulkAllocate  = false;
  bulkTarget: 'employees' | 'team' | 'designation' = 'employees';
  bulkSelectedEmps: Set<string> = new Set();
  bulkTeamId        = '';
  bulkDesignation   = '';
  bulkYear          = new Date().getFullYear();
  bulkAllocations   = [
    { leaveType: 'casual',   label: 'Casual Leave',     color: '#334155', enabled: true,  days: 0 },
    { leaveType: 'sick',     label: 'Sick Leave',       color: '#059669', enabled: true,  days: 0 },
    { leaveType: 'earned',   label: 'Earned Leave',     color: '#d97706', enabled: false, days: 0 },
    { leaveType: 'optional', label: 'Optional Holiday', color: '#7c3aed', enabled: false, days: 0 },
  ];
  bulkEmpSearch     = '';
  bulkSubmitting    = false;

  showDirAllocate   = false;
  dirAllocateForm: any = { employeeId: '', leaveType: 'casual', days: 0 };
  dirAllocating     = false;

  dirShowRegularized = false;

  showMarkAtt       = false;
  markAttForm: any  = { date: '', checkIn: '09:00', checkOut: '18:00', status: 'present', notes: '' };
  markAttSubmitting = false;

  showEditEmp      = false;
  editForm: any    = {};
  editSubmitting   = false;

  showAddEmp       = false;
  addForm: any     = { firstName: '', lastName: '', email: '', employeeId: '', phone: '', departmentId: '', position: '', role: 'employee', location: 'Office' };
  addSubmitting    = false;

  roleOptions      = ['employee','manager','hr','hr_admin','admin'];
  locationOptions  = ['Office','Remote','Hybrid'];
  statusOptions    = ['active','inactive','on_leave'];
  leaveTypeOptions = [
    { label: 'Casual Leave',     value: 'casual'   },
    { label: 'Sick Leave',       value: 'sick'     },
    { label: 'Earned Leave',     value: 'earned'   },
    { label: 'Optional Holiday', value: 'optional' },
  ];
  attStatusOptions = [
    { label: 'Present',  value: 'present'  },
    { label: 'Absent',   value: 'absent'   },
    { label: 'Late',     value: 'late'     },
    { label: 'Half Day', value: 'half-day' },
    { label: 'On Leave', value: 'on-leave' },
  ];

  constructor(
    private api: ApiService,
    private headerService: HeaderService,
    private snack: SnackbarService,
    private auth: AuthService,
    public perm: PermissionService,
    private confirm: ConfirmDialogService,
  ) {}

  ngOnInit(): void {
    this.headerService.setTitle('People');
    this.loadCoreData();
  }

  isLeavesTab(): boolean          { return this.pageTab === 'leaves'; }
  isLeavesApprovalsTab(): boolean { return this.leaveSubTab === 'approvals'; }
  isLeavesBalancesTab(): boolean  { return this.leaveSubTab === 'balances'; }

  switchPageTab(tab: 'people' | 'attendance' | 'leaves'): void {
    this.pageTab = tab;
    if (tab === 'attendance' && this.dirRecords.length === 0) this.loadDirAttendance();
    if (tab === 'leaves') { this.allLeavesLoading = true; this.loadAllLeaves(); }
  }

  switchLeaveSubTab(tab: 'approvals' | 'balances'): void {
    this.leaveSubTab = tab;
    if (tab === 'balances' && this.allBalances.length === 0) this.loadAllBalances();
  }

  refreshLeaveTab(): void {
    if (this.leaveSubTab === 'approvals') this.loadAllLeaves();
    else this.loadAllBalances();
  }

  resetLeavePagination(): void {
    this.leaveCurrentPage = 1;
  }

  setLeaveStatusFilter(status: string): void {
    this.leaveStatusFilter = status;
    this.resetLeavePagination();
  }

  setLeavePageSize(size: number | string): void {
    this.leavePageSize = Number(size) || 10;
    this.resetLeavePagination();
  }

  goToLeavePage(page: number): void {
    this.leaveCurrentPage = Math.min(Math.max(page, 1), this.leaveTotalPages);
  }

  toggleDirRegularized(): void {
    this.dirShowRegularized = !this.dirShowRegularized;
    if (this.dirShowRegularized) this.dirFilter = 'all';
  }


  loadCoreData(): void {
    this.loading = true;
    forkJoin({
      employees:   this.api.get('/employees').pipe(catchError(() => of([]))),
      departments: this.api.get('/departments').pipe(catchError(() => of([]))),
      teams:       this.api.get('/teams').pipe(catchError(() => of([]))),
      shifts:      this.api.get('/shifts').pipe(catchError(() => of([]))),
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(({ employees, departments, teams, shifts }) => {
      this.employees   = this.extractList(employees);
      this.departments = this.extractList(departments);
      this.teams       = this.extractList(teams);
      this.shifts      = this.extractList(shifts);
      this.loading     = false;
      if (this.employees.length > 0) this.selectEmployee(this.employees[0]);
    });
  }


  selectEmployee(emp: any): void {
    if (this.selectedEmp?.id === emp.id) return;
    this.selectedEmp     = emp;
    this.activeDetailTab = 'overview';
    this.empAttendance   = [];
    this.empLeaves       = [];
    this.empBalances     = [];
    this.empPending      = [];
    this.empAssets       = [];
    this.empTeams        = [];
    this.detailLoading   = true;

    forkJoin({
      leaves:   this.api.get('/leaves').pipe(catchError(() => of([]))),
      balances: this.api.get('/leave-balances').pipe(catchError(() => of([]))),
      assets:   this.api.get('/assets').pipe(catchError(() => of([]))),
      teams:    this.api.get(`/teams/employee/${emp.id}`).pipe(catchError(() => of([]))),
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(({ leaves, balances, assets, teams }) => {
      const id           = emp.id;
      const allLeaves    = this.extractList(leaves).filter((l: any) => l.employeeId === id);
      this.empLeaves     = allLeaves.filter((l: any) => l.status !== 'pending');
      this.empPending    = allLeaves.filter((l: any) => l.status === 'pending');
      this.empBalances   = this.extractList(balances).filter((b: any) => b.employeeId === id);
      this.empAssets     = this.extractList(assets).filter((a: any) => a.assignedTo === id || a.employeeId === id);
      this.empTeams      = this.extractList(teams);
      this.detailLoading = false;
      this.loadEmpAttendanceMonth(emp.id, this.attMonth);
    });
  }


  loadEmpAttendanceMonth(empId: string, month: string): void {
    this.empAttLoading = true;
    const [y, m] = month.split('-').map(Number);
    const days   = new Date(y, m, 0).getDate();
    const today  = this.todayStr();

    const requests: { [key: string]: Observable<any> } = {};
    for (let d = 1; d <= days; d++) {
      const dateStr = `${month}-${String(d).padStart(2, '0')}`;
      if (dateStr > today) break;
      requests[dateStr] = this.api.get(`/attendance?date=${dateStr}`).pipe(catchError(() => of([])));
    }

    if (Object.keys(requests).length === 0) { this.empAttLoading = false; return; }

    forkJoin(requests).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((results) => {
      const records: any[] = [];
      Object.values(results).forEach((dayData: any) => {
        const list = this.extractList(dayData);
        const rec  = list.find((r: any) => r.employeeId === empId);
        if (rec) records.push(rec);
      });
      this.empAttendance = records.sort((a, b) => b.date?.localeCompare?.(a.date) ?? 0);
      this.empAttLoading = false;
    });
  }

  onAttMonthChange(): void {
    if (this.selectedEmp) this.loadEmpAttendanceMonth(this.selectedEmp.id, this.attMonth);
  }


  approveLeave(leave: any, source: 'emp' | 'dir' = 'emp'): void {
    this.approvingId = leave.id;
    this.api.put(`/leaves/${leave.id}/status`, { status: 'approved' }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.snack.showSuccess('Leave approved');
        leave.status = 'approved';
        if (source === 'emp') {
          this.empPending = this.empPending.filter(l => l.id !== leave.id);
          this.empLeaves.unshift({ ...leave });
        } else {
          this.allLeaves = this.allLeaves.map(l => l.id === leave.id ? { ...l, status: 'approved' } : l);
        }
        this.approvingId = null;
      },
      error: () => { this.snack.showFailure('Failed to approve'); this.approvingId = null; }
    });
  }

  rejectLeave(leave: any, source: 'emp' | 'dir' = 'emp'): void {
    const reason = source === 'dir' ? this.leaveRejectReason : this.rejectReason;
    if (!reason.trim()) { this.snack.showFailure('Enter a rejection reason'); return; }
    this.api.put(`/leaves/${leave.id}/status`, { status: 'rejected', reason }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.snack.showSuccess('Leave rejected');
        leave.status = 'rejected';
        if (source === 'emp') {
          this.empPending = this.empPending.filter(l => l.id !== leave.id);
          this.empLeaves.unshift({ ...leave });
          this.rejectingId = null; this.rejectReason = '';
        } else {
          this.allLeaves = this.allLeaves.map(l => l.id === leave.id ? { ...l, status: 'rejected' } : l);
          this.leaveRejectingId = null; this.leaveRejectReason = '';
        }
      },
      error: () => this.snack.showFailure('Failed to reject')
    });
  }

  submitAllocate(): void {
    if (!this.selectedEmp) return;
    this.allocating = true;
    this.api.post('/leave-balances/allocate', {
      employeeId: this.selectedEmp.id,
      leaveType:  this.allocateForm.leaveType,
      days:       +this.allocateForm.days,
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.snack.showSuccess('Leave days allocated');
        this.showAllocate = false; this.allocateForm = { leaveType: 'casual', days: 0 }; this.allocating = false;
        this.api.get('/leave-balances').pipe(catchError(() => of([]))).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((d: any) => {
          this.empBalances = this.extractList(d).filter((b: any) => b.employeeId === this.selectedEmp.id);
        });
      },
      error: () => { this.snack.showFailure('Allocation failed'); this.allocating = false; }
    });
  }


  loadDirAttendance(): void {
    this.dirLoading = true;
    this.api.get(`/attendance?date=${this.dirDate}`).pipe(catchError(() => of([]))).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      this.dirRecords = this.extractList(data);
      this.dirLoading = false;
    });
  }

  get filteredDirRecords(): any[] {
    let list = this.dirRecords;
    if (this.dirShowRegularized) {
      list = list.filter(r => r.regularized || r.isRegularized || r.type === 'regularized');
    } else if (this.dirFilter !== 'all') {
      list = list.filter(r => r.status === this.dirFilter);
    }
    const q = this.dirSearch.toLowerCase().trim();
    if (q) list = list.filter(r => {
      const emp = this.employees.find(e => e.id === r.employeeId);
      return `${emp?.firstName || ''} ${emp?.lastName || ''}`.toLowerCase().includes(q) ||
             (emp?.employeeId || '').toLowerCase().includes(q);
    });
    return list;
  }

  dirSummaryCount(status: string): number {
    return this.dirRecords.filter(r => r.status === status).length;
  }

  get unmarkedEmployees(): any[] {
    const markedIds = new Set(this.dirRecords.map(r => r.employeeId));
    return this.employees.filter(e => !markedIds.has(e.id));
  }

  markAttForDir(emp: any): void {
    this.markAttForm = { date: this.dirDate, checkIn: '09:00', checkOut: '18:00', status: 'present', notes: '' };
    this.selectedEmp = emp;
    this.showMarkAtt = true;
  }


  loadAllLeaves(): void {
    this.allLeavesLoading = true;
    this.api.get('/leaves').pipe(catchError(() => of([]))).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      this.allLeaves        = this.extractList(data);
      this.allLeavesLoading = false;
    });
  }

  loadAllBalances(): void {
    this.balancesLoading = true;
    this.api.get('/leave-balances').pipe(catchError(() => of([]))).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      this.allBalances    = this.extractList(data);
      this.balancesLoading = false;
    });
  }

  getBalRemaining(empId: string, type: string): number {
    const b = this.allBalances.find(x => x.employeeId === empId && x.leaveType === type);
    return b ? (b.remainingDays ?? b.balance ?? 0) : 0;
  }

  getBalTotal(empId: string, type: string): number {
    const b = this.allBalances.find(x => x.employeeId === empId && x.leaveType === type);
    return b ? (b.totalDays ?? b.allocated ?? 0) : 0;
  }

  get uniqueDesignations(): string[] {
    const s = new Set<string>();
    this.employees.forEach(e => { if (e.designation) s.add(e.designation); else if (e.role) s.add(e.role); });
    return [...s].sort();
  }

  get filteredBalanceEmployees(): any[] {
    const q = this.balanceSearch.toLowerCase().trim();
    if (!q) return this.employees;
    return this.employees.filter(e =>
      `${e.firstName} ${e.lastName}`.toLowerCase().includes(q) ||
      (e.employeeId || '').toLowerCase().includes(q)
    );
  }

  openBulkAllocate(emp?: any): void {
    this.bulkTarget         = emp ? 'employees' : 'employees';
    this.bulkSelectedEmps   = emp ? new Set([emp.id]) : new Set();
    this.bulkTeamId         = '';
    this.bulkDesignation    = '';
    this.bulkEmpSearch      = '';
    this.bulkYear           = new Date().getFullYear();
    this.bulkAllocations.forEach(a => { a.days = 0; a.enabled = a.leaveType === 'casual' || a.leaveType === 'sick'; });
    this.showBulkAllocate   = true;
  }

  get bulkFilteredEmps(): any[] {
    const q = this.bulkEmpSearch.toLowerCase().trim();
    if (!q) return this.employees;
    return this.employees.filter(e =>
      `${e.firstName} ${e.lastName}`.toLowerCase().includes(q) ||
      (e.employeeId || '').toLowerCase().includes(q)
    );
  }

  toggleBulkEmp(id: string): void {
    if (this.bulkSelectedEmps.has(id)) this.bulkSelectedEmps.delete(id);
    else this.bulkSelectedEmps.add(id);
  }

  selectAllBulkEmps(): void {
    if (this.bulkSelectedEmps.size === this.employees.length) {
      this.bulkSelectedEmps = new Set();
    } else {
      this.bulkSelectedEmps = new Set(this.employees.map(e => e.id));
    }
  }

  get bulkResolvedEmployeeIds(): string[] {
    if (this.bulkTarget === 'employees') return [...this.bulkSelectedEmps];
    if (this.bulkTarget === 'team')
      return this.employees.filter(e => e.teamId === this.bulkTeamId || (e.teams || []).some((t: any) => t.id === this.bulkTeamId || t === this.bulkTeamId)).map(e => e.id);
    if (this.bulkTarget === 'designation')
      return this.employees.filter(e => (e.designation || e.role) === this.bulkDesignation).map(e => e.id);
    return [];
  }

  get bulkPreviewCount(): number { return this.bulkResolvedEmployeeIds.length; }

  submitBulkAllocate(): void {
    const ids = this.bulkResolvedEmployeeIds;
    const allocations = this.bulkAllocations.filter(a => a.enabled && a.days > 0).map(a => ({ leaveType: a.leaveType, days: a.days }));
    if (!ids.length) { this.snack.showFailure('No employees selected'); return; }
    if (!allocations.length) { this.snack.showFailure('Set days for at least one leave type'); return; }
    this.bulkSubmitting = true;
    this.api.post('/leave-balances/bulk-allocate', { employeeIds: ids, allocations, year: this.bulkYear }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        this.snack.showSuccess(`Allocated to ${res.employees ?? ids.length} employee(s)`);
        this.showBulkAllocate = false;
        this.bulkSubmitting   = false;
        this.loadAllBalances();
      },
      error: () => { this.snack.showFailure('Bulk allocation failed'); this.bulkSubmitting = false; }
    });
  }

  openDirAllocate(emp?: any): void {
    this.dirAllocateForm = { employeeId: emp?.id || '', leaveType: 'casual', days: 0 };
    this.showDirAllocate = true;
  }

  submitDirAllocate(): void {
    if (!this.dirAllocateForm.employeeId) { this.snack.showFailure('Select an employee'); return; }
    this.dirAllocating = true;
    this.api.post('/leave-balances/allocate', {
      employeeId: this.dirAllocateForm.employeeId,
      leaveType:  this.dirAllocateForm.leaveType,
      days:       +this.dirAllocateForm.days,
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.snack.showSuccess('Leave days allocated');
        this.showDirAllocate  = false;
        this.dirAllocateForm  = { employeeId: '', leaveType: 'casual', days: 0 };
        this.dirAllocating    = false;
        this.loadAllBalances();
      },
      error: () => { this.snack.showFailure('Allocation failed'); this.dirAllocating = false; }
    });
  }

  get filteredAllLeaves(): any[] {
    let list = this.allLeaves;
    if (this.leaveStatusFilter !== 'all') list = list.filter(l => l.status === this.leaveStatusFilter);
    const q = this.leaveSearch.toLowerCase().trim();
    if (q) list = list.filter(l => {
      const emp = this.employees.find(e => e.id === l.employeeId);
      return `${emp?.firstName || ''} ${emp?.lastName || ''}`.toLowerCase().includes(q) ||
             (l.leaveType || '').toLowerCase().includes(q);
    });
    return [...list].sort((a, b) => new Date(b.createdAt || b.startDate).getTime() - new Date(a.createdAt || a.startDate).getTime());
  }

  get leaveTotalPages(): number {
    return Math.max(1, Math.ceil(this.filteredAllLeaves.length / this.leavePageSize));
  }

  get clampedLeavePage(): number {
    return Math.min(Math.max(this.leaveCurrentPage, 1), this.leaveTotalPages);
  }

  get paginatedAllLeaves(): any[] {
    const start = (this.clampedLeavePage - 1) * this.leavePageSize;
    return this.filteredAllLeaves.slice(start, start + this.leavePageSize);
  }

  get leavePageStart(): number {
    return this.filteredAllLeaves.length === 0 ? 0 : ((this.clampedLeavePage - 1) * this.leavePageSize) + 1;
  }

  get leavePageEnd(): number {
    return Math.min(this.clampedLeavePage * this.leavePageSize, this.filteredAllLeaves.length);
  }

  get leavePageNumbers(): number[] {
    const total = this.leaveTotalPages;
    const current = this.clampedLeavePage;
    const start = Math.max(1, current - 2);
    const end = Math.min(total, start + 4);
    const adjustedStart = Math.max(1, end - 4);
    const pages: number[] = [];
    for (let page = adjustedStart; page <= end; page++) pages.push(page);
    return pages;
  }

  get pendingLeavesCount(): number {
    return this.allLeaves.filter(l => l.status === 'pending').length;
  }


  private _markAttLastClick = 0;
  submitMarkAtt(): void {
    const now = Date.now();
    if (now - this._markAttLastClick < 600) return;
    this._markAttLastClick = now;
    if (!this.selectedEmp) return;
    this.markAttSubmitting = true;
    this.api.post('/attendance', {
      employeeId: this.selectedEmp.id,
      date: this.markAttForm.date, checkIn: this.markAttForm.checkIn,
      checkOut: this.markAttForm.checkOut, status: this.markAttForm.status, notes: this.markAttForm.notes,
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.snack.showSuccess('Attendance marked');
        this.showMarkAtt = false; this.markAttSubmitting = false;
        this.markAttForm = { date: '', checkIn: '09:00', checkOut: '18:00', status: 'present', notes: '' };
        this.loadEmpAttendanceMonth(this.selectedEmp.id, this.attMonth);
        if (this.pageTab === 'attendance') this.loadDirAttendance();
      },
      error: () => { this.snack.showFailure('Failed to mark attendance'); this.markAttSubmitting = false; }
    });
  }


  openEditEmp(): void {
    if (!this.selectedEmp) return;
    this.editForm = { ...this.selectedEmp }; this.showEditEmp = true;
  }

  submitEditEmp(): void {
    this.editSubmitting = true;
    this.api.put(`/employees/${this.selectedEmp.id}`, this.editForm).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        this.snack.showSuccess('Profile updated');
        const updated = res?.data || res;
        const idx = this.employees.findIndex(e => e.id === this.selectedEmp.id);
        if (idx >= 0) this.employees[idx] = { ...this.employees[idx], ...updated };
        this.selectedEmp = { ...this.selectedEmp, ...updated };
        this.showEditEmp = false; this.editSubmitting = false;
      },
      error: () => { this.snack.showFailure('Update failed'); this.editSubmitting = false; }
    });
  }

  submitAddEmp(): void {
    this.addSubmitting = true;
    this.api.post('/employees', this.addForm).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        this.snack.showSuccess('Employee added');
        const emp = res?.data || res;
        this.employees.unshift(emp);
        this.showAddEmp = false; this.addSubmitting = false;
        this.addForm = { firstName: '', lastName: '', email: '', employeeId: '', phone: '', departmentId: '', position: '', role: 'employee', location: 'Office' };
        this.selectEmployee(emp);
      },
      error: () => { this.snack.showFailure('Failed to add employee'); this.addSubmitting = false; }
    });
  }

  deleteEmployee(emp: any): void {
    this.confirm.confirm(`Delete ${emp.firstName} ${emp.lastName}?`, { message: 'This cannot be undone.' }).then(ok => {
      if (!ok) return;
      this.api.delete(`/employees/${emp.id}`).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => {
          this.snack.showSuccess('Employee removed');
          this.employees = this.employees.filter(e => e.id !== emp.id);
          if (this.selectedEmp?.id === emp.id) {
            this.selectedEmp = this.employees[0] || null;
            if (this.selectedEmp) this.selectEmployee(this.selectedEmp);
          }
        },
        error: () => this.snack.showFailure('Delete failed')
      });
    });
  }


  get filteredEmployees(): any[] {
    let list = this.employees;
    if (this.filterStatus !== 'all') list = list.filter(e => e.status === this.filterStatus);
    const q = this.searchText.toLowerCase().trim();
    if (q) list = list.filter(e =>
      `${e.firstName} ${e.lastName}`.toLowerCase().includes(q) ||
      (e.email || '').toLowerCase().includes(q) ||
      (e.employeeId || '').toLowerCase().includes(q)
    );
    return list;
  }

  get filteredAttendance(): any[] {
    let list = this.empAttendance;
    if (this.attFilter !== 'all') list = list.filter(r => r.status === this.attFilter);
    return list;
  }

  get presentDaysThisMonth(): number {
    return this.empAttendance.filter(r => r.date?.startsWith(this.attMonth) && r.status === 'present').length;
  }

  get totalLeaveBalance(): number {
    return this.empBalances.reduce((s, b) => s + (b.remainingDays ?? b.balance ?? 0), 0);
  }

  attCountForStatus(status: string): number {
    return this.empAttendance.filter(r => r.date?.startsWith(this.attMonth) && r.status === status).length;
  }


  getDeptName(id: any): string {
    return this.departments.find(d => String(d.id) === String(id))?.name || '—';
  }

  getEmpName(empId: string): string {
    const e = this.employees.find(x => x.id === empId);
    return e ? `${e.firstName} ${e.lastName}` : '—';
  }

  getEmpById(empId: string): any {
    return this.employees.find(x => x.id === empId);
  }

  getTeamNames(): string {
    return this.empTeams.map((t: any) => t.name).join(', ') || '—';
  }

  getInitials(emp: any): string {
    return `${(emp?.firstName || '')[0] || ''}${(emp?.lastName || '')[0] || ''}`.toUpperCase();
  }

  getAvatarGradient(name: string): string {
    const g = [
      'linear-gradient(135deg,#3f4f66,#334155)',
      'linear-gradient(135deg,#55617a,#475569)',
      'linear-gradient(135deg,#0f766e,#14b8a6)',
      'linear-gradient(135deg,#166534,#16a34a)',
      'linear-gradient(135deg,#92400e,#b45309)',
      'linear-gradient(135deg,#7c3a5a,#9d5a7a)',
    ];
    let h = 0;
    for (let i = 0; i < (name || '').length; i++) h = (name.charCodeAt(i) + ((h << 5) - h));
    return g[Math.abs(h) % g.length];
  }

  getStatusClass(s: string): string {
    return ({ active: 'st-active', inactive: 'st-inactive', on_leave: 'st-leave' } as any)[s] || 'st-inactive';
  }

  getAttStatusClass(s: string): string {
    return ({ present: 'att-present', absent: 'att-absent', late: 'att-late', 'half-day': 'att-half', 'on-leave': 'att-leave' } as any)[s] || '';
  }

  getLeaveStatusClass(s: string): string {
    return ({ approved: 'lv-approved', rejected: 'lv-rejected', pending: 'lv-pending' } as any)[s] || '';
  }

  getBalanceColor(type: string): string {
    return ({ casual: '#334155', sick: '#065f46', earned: '#885200', optional: '#6b21a8' } as any)[type] || '#374151';
  }

  formatDate(d: string): string {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return d; }
  }

  formatTime(t: string): string {
    if (!t) return '—';
    if (t.length <= 5) return t;
    try { return new Date(`1970-01-01T${t}`).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }); } catch { return t; }
  }

  todayStr(): string {
    return new Date().toISOString().split('T')[0];
  }

  currentMonthStr(): string {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  }

  isAdminOrHR(): boolean { return this.perm.isAdminOrHR(); }
  canEdit(): boolean     { return this.perm.isAdminOrHR(); }
  canDelete(): boolean   { return this.perm.isAdminOrHR(); }

  private extractList(data: any): any[] {
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.data)) return data.data;
    if (data && Array.isArray(data.content)) return data.content;
    return [];
  }
}
